import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Send, Loader2, RotateCcw, ArrowRight, BarChart3, Shield, Users, FileText, AlertTriangle, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTED_PROMPTS = [
  { text: "Which plans should I prioritise renewing this week?", label: "Renewal priorities", icon: Shield, color: '#ef4444' },
  { text: "Summarise our current risk exposure across the portfolio", label: "Risk summary", icon: AlertTriangle, color: '#f59e0b' },
  { text: "What's the revenue breakdown for our top 5 groups?", label: "Top groups", icon: BarChart3, color: '#10b981' },
  { text: "Which managers have the most renewals coming up?", label: "Manager renewals", icon: Users, color: '#3b82f6' },
  { text: "Help me draft a renewal engagement email for an expired contract", label: "Draft email", icon: FileText, color: '#8b5cf6' },
  { text: "Show me the NPS detractors I should be worried about", label: "NPS concerns", icon: MessageSquare, color: '#ec4899' },
];

// Safely render a line with bold (**...**) segments without using dangerouslySetInnerHTML.
const renderInline = (line, keyPrefix) => {
  // Split on **bold** tokens — odd-indexed segments are the bold content.
  const parts = line.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={`${keyPrefix}-b-${i}`}>{part}</strong>
      : <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>
  );
};

const formatMessage = (text) => {
  // Build a stable key from line content + position so re-renders are predictable
  // even if the same text appears multiple times.
  return text.split('\n').map((line, i) => {
    const key = `ln-${i}-${line.slice(0, 16)}`;
    // Numbered lists
    if (/^\d+\.\s/.test(line.trim())) {
      return <li key={key} className="ml-4 mb-1 list-decimal">{renderInline(line.replace(/^\d+\.\s/, ''), key)}</li>;
    }
    // Bullet points
    if (/^[-•]\s/.test(line.trim())) {
      return <li key={key} className="ml-4 mb-1 list-disc">{renderInline(line.replace(/^[-•]\s/, ''), key)}</li>;
    }
    // Empty lines
    if (!line.trim()) return <div key={key} className="h-2" />;
    // Regular text
    return <p key={key} className="mb-1">{renderInline(line, key)}</p>;
  });
};

const TKAnalyst = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!loading) inputRef.current?.focus();
  }, [loading]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, { message: text, sessionId });
      setSessionId(res.data.sessionId);
      setMessages(prev => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { id: `a-${Date.now()}-err`, role: 'assistant', content: "I'm having trouble connecting right now. Give me a moment and try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setInput('');
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="tk-analyst-view">
      {!hasMessages ? (
        /* ───── Welcome State ───── */
        <div className="flex-1 flex flex-col items-center pt-[10vh] px-6 overflow-y-auto">
          <div className="w-full max-w-[640px]">
            {/* Greeting */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-[12px] bg-[#171717] flex items-center justify-center shadow-md">
                  <span className="text-white text-sm font-bold tracking-tight">TK</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-[#171717] font-heading tracking-tight">TK Analyst</h1>
                  <p className="text-[11px] text-[#A3A3A3] tracking-wide uppercase font-medium">Live Portfolio Intelligence</p>
                </div>
              </div>
              <p className="text-[15px] text-[#525252] leading-relaxed">
                Here to help you make sense of the data. I've got the latest numbers across the portfolio — ask me anything.
              </p>
            </div>

            {/* Input */}
            <div className="relative mb-8">
              <textarea
                ref={inputRef}
                data-testid="analyst-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                placeholder="What would you like to know?"
                rows={2}
                className="w-full px-5 py-4 pr-14 rounded-[16px] border border-[#E5E5E5] bg-white text-[15px] text-[#171717] placeholder-[#B3B3B3] focus:outline-none focus:border-[#525252] shadow-[0_1px_8px_rgba(0,0,0,0.04)] transition-all resize-none"
              />
              <button
                data-testid="analyst-send"
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                className="absolute right-3 bottom-3 w-10 h-10 rounded-[10px] bg-[#171717] text-white flex items-center justify-center hover:bg-[#262626] transition-all disabled:opacity-20 disabled:hover:bg-[#171717]"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>

            {/* Prompt Cards */}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#A3A3A3] mb-3">Try asking</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SUGGESTED_PROMPTS.map((prompt) => {
                  const Icon = prompt.icon;
                  return (
                    <button
                      key={prompt.label}
                      data-testid={`prompt-${prompt.label.replace(/\s/g, '-')}`}
                      onClick={() => sendMessage(prompt.text)}
                      disabled={loading}
                      className="flex items-center gap-3 px-4 py-3 rounded-[12px] border border-[#F0F0F0] bg-white text-left hover:border-[#D4D4D4] hover:shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all group disabled:opacity-50"
                    >
                      <div className="w-8 h-8 rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${prompt.color}10` }}>
                        <Icon className="w-4 h-4" style={{ color: prompt.color }} strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-[#171717] group-hover:text-[#000] transition-colors truncate">{prompt.label}</p>
                        <p className="text-[11px] text-[#A3A3A3] truncate">{prompt.text}</p>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-[#D4D4D4] group-hover:text-[#737373] transition-colors flex-shrink-0" strokeWidth={1.5} />
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="text-[10px] text-[#C4C4C4] text-center mt-10">
              Connected to live portfolio data · Never used for model training
            </p>
          </div>
        </div>
      ) : (
        /* ───── Chat State ───── */
        <>
          {/* Chat Header */}
          <div className="flex items-center justify-between px-6 py-2.5 border-b border-[#F0F0F0] bg-white sticky top-0 z-10">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-[8px] bg-[#171717] flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">TK</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#171717] leading-tight">TK Analyst</p>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <p className="text-[10px] text-[#A3A3A3]">Live data</p>
                </div>
              </div>
            </div>
            <button
              data-testid="new-chat-btn"
              onClick={handleNewChat}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-medium text-[#737373] hover:text-[#171717] hover:bg-[#F8F9FA] border border-transparent hover:border-[#E5E5E5] transition-all"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
              New chat
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-[740px] mx-auto px-6 py-6 space-y-5">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-[8px] bg-[#171717] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-[10px] font-bold">TK</span>
                    </div>
                  )}
                  <div className={`${msg.role === 'user'
                    ? 'bg-[#171717] text-white rounded-[16px] rounded-br-[4px] px-5 py-3 max-w-[75%]'
                    : 'flex-1 min-w-0'
                  }`}>
                    {msg.role === 'user' ? (
                      <p className="text-[14px] leading-relaxed">{msg.content}</p>
                    ) : (
                      <div className="text-[14px] leading-[1.7] text-[#2a2a2a] prose-sm">
                        {formatMessage(msg.content)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-[8px] bg-[#171717] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-[10px] font-bold">TK</span>
                  </div>
                  <div className="flex items-center gap-1.5 py-2">
                    <span className="w-2 h-2 rounded-full bg-[#D4D4D4] animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-[#D4D4D4] animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-[#D4D4D4] animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Chat Input */}
          <div className="border-t border-[#F0F0F0] bg-white">
            <div className="max-w-[740px] mx-auto px-6 py-3">
              <div className="relative">
                <input
                  ref={inputRef}
                  data-testid="analyst-input-chat"
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') sendMessage(input); }}
                  placeholder="Ask a follow-up..."
                  className="w-full px-4 py-3 pr-12 rounded-[12px] border border-[#E5E5E5] bg-[#FAFAFA] text-sm text-[#171717] placeholder-[#B3B3B3] focus:outline-none focus:border-[#525252] focus:bg-white transition-all"
                />
                <button
                  data-testid="analyst-send-chat"
                  onClick={() => sendMessage(input)}
                  disabled={!input.trim() || loading}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-[8px] bg-[#171717] text-white flex items-center justify-center hover:bg-[#262626] transition-all disabled:opacity-20"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.5} />}
                </button>
              </div>
              <p className="text-[10px] text-[#D4D4D4] text-center mt-1.5">Data stays private · Never used for model training</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TKAnalyst;
